/**
 * The two keyword numbers, and the words that keep them apart.
 *
 * They come from different places and mean different things, so nothing here
 * blends them and no caller can render one as the other.
 *
 *   difficulty  OURS. kw_difficulty, computed by ads-app service/difficulty.py
 *               from data the daily sweep already stored: top-10 rating power,
 *               top-25 churn, SERP depth, and how much of page one carries the
 *               term in its own app name. Four percentiles across the keywords
 *               observed on that day, weighted 45/20/20/15. The weights are
 *               declared, not fitted, and the score is RELATIVE to that day's
 *               corpus. It is not comparable to a vendor's difficulty and must
 *               never be printed beside one.
 *
 *   apple       APPLE'S. kw_apple_popularity, Apple's own searchPopularity1to100
 *               from the Apple Ads Platform API. Quotable as Apple's figure.
 *               It covers the head of each genre and nothing else: Apple caps
 *               its board at 500 terms per country-genre over 15 genres, so an
 *               absent number means "outside the top 500 of its genre", NEVER
 *               "unpopular".
 *
 *   hints       OURS. kw_hints, one row per keyword: Apple's own search-suggestion
 *               ORDER, read as the shortest prefix Apple offers the term after and
 *               where in the ten. Not Apple's popularity figure, not a count of
 *               searches. A null score means Apple never offers the term.
 *
 * Pure logic in its own module because this repository's unit suite runs in the
 * node environment with no jsdom and no testing-library, so a band or a caption
 * that drifts is only catchable here. Same reason as keyword-rank-tiers.ts.
 */

import { formatNumber } from "@/lib/utils";

export type DifficultyTier = { label: string; chip: string; terminalChip: string };

export type KeywordDifficulty = {
  score: number;
  /** Median rating count of positions 1-10. Null when no top-10 app carries a rating row. */
  top10_median_ratings: number | null;
  /** Mean |change in rank| across the top 25 since the previous sweep. Null on a first sweep. */
  rank_churn: number | null;
  /** Positions the store returned for this keyword-day. */
  field_depth: number;
  /** Share of the top 10 whose app name contains the term, 0..1. */
  brand_lock: number | null;
  /** How many of the top 10 carry a rating row at all. */
  top10_rated: number | null;
  /** Keywords the percentiles were taken over. */
  corpus_size: number;
  provisional: boolean;
  provisional_reason: string | null;
};

export type ApplePopularity = {
  /** Apple's 1-100 score across all genres in the country. */
  popularity_1to100: number | null;
  /** The 1-5 indicator the Search Ads dashboard shows. */
  popularity_1to5: number | null;
  /** 1..500 within its genre. Apple's cap, not ours. */
  rank_in_genre: number | null;
  genre: string | null;
  period_start: string | null;
};

export type KeywordHints = {
  country_code: string;
  observed_on: string;
  term_len: number;
  /** How many suggestions Apple returns for the full term; 0 when it is not a query at all. */
  hints_at_term: number | null;
  /** Where the term sits in Apple's list when typed in full. Null when it is not offered. */
  rank_at_term: number | null;
  /** Shortest prefix after which Apple offers the term. Null when it never does. */
  prefix_len: number | null;
  /** 1-based position in the ten suggestions at that prefix. */
  rank_at_prefix: number | null;
  /** Our 1-100 ordinal from the two above. Null exactly when prefix_len is null. */
  hint_score: number | null;
};

/**
 * Four bands over the 0-100 estimate.
 *
 * Deliberately the same visual grammar as rankTier, inverted: a HIGH difficulty
 * is the unwelcome end, so the filled chip goes to "Very hard" rather than to
 * the top band being good. The number is always printed inside the chip, so the
 * band is a second encoding of something already readable and nothing here is
 * carried by colour alone.
 */
export function difficultyTier(score: number): DifficultyTier {
  if (score >= 75) {
    return {
      label: "Very hard",
      chip: "bg-rose-600 text-white dark:bg-rose-500 dark:text-rose-950",
      terminalChip: "bg-emerald-400 text-black",
    };
  }
  if (score >= 50) {
    return {
      label: "Hard",
      chip:
        "bg-rose-500/10 text-rose-700 ring-1 ring-rose-500/25 " +
        "dark:text-rose-300 dark:ring-rose-500/35",
      terminalChip: "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/50",
    };
  }
  if (score >= 25) {
    return {
      label: "Moderate",
      chip:
        "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/25 " +
        "dark:text-amber-300 dark:ring-amber-500/35",
      terminalChip: "bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30",
    };
  }
  return {
    label: "Easy",
    chip: "bg-muted/60 text-foreground/80 ring-1 ring-border",
    terminalChip: "text-emerald-400/80 ring-1 ring-emerald-700/60",
  };
}

/**
 * The whole honest sentence behind one difficulty chip, for its title.
 *
 * It leads with whose number it is, because that is the thing a reader could
 * otherwise get wrong, and it quotes the components rather than only the score:
 * a 0-100 nobody can take apart is not checkable.
 */
export function difficultyTitle(d: KeywordDifficulty, keyword: string): string {
  const tier = difficultyTier(d.score);
  const parts = [
    `Shortimize difficulty ${d.score}/100 (${tier.label.toLowerCase()}) for “${keyword}” — our estimate, not Apple's and not an industry figure.`,
  ];
  if (d.top10_median_ratings !== null) {
    const rated =
      d.top10_rated !== null && d.top10_rated < 10 ? ` (${d.top10_rated} of 10 rated)` : "";
    // formatNumber is the app's own compact formatter (lib/utils.ts), used across every
    // other surface. A second one here would read 630k where the page beside it reads
    // 630.3K.
    parts.push(`Top ten median ${formatNumber(d.top10_median_ratings)} ratings${rated}.`);
  }
  if (d.brand_lock !== null && d.brand_lock > 0) {
    parts.push(`${Math.round(d.brand_lock * 10)} of the top 10 carry the term in their name.`);
  }
  if (d.rank_churn !== null) {
    parts.push(`The top 25 moved ${round1(d.rank_churn)} places since the previous sweep.`);
  }
  parts.push(`Page depth ${d.field_depth}.`);
  parts.push(`Relative to the ${d.corpus_size} keywords swept that day.`);
  if (d.provisional && d.provisional_reason) parts.push(`Provisional: ${d.provisional_reason}.`);
  return parts.join(" ");
}

/** The whole sentence behind one popularity number: whose it is, and how it was read. */
export function hintsTitle(h: KeywordHints, keyword: string): string {
  const parts = [
    `Popularity ${h.hint_score}/100 for “${keyword}” — our estimate from Apple's own search-suggestion order, not Apple's popularity figure and not a count of searches.`,
    `Apple offers this term after ${h.prefix_len} of ${h.term_len} characters, at position ${h.rank_at_prefix} of 10.`,
  ];
  if (h.rank_at_term !== null) parts.push(`Typed in full it is suggestion ${h.rank_at_term}.`);
  parts.push(`Observed ${h.observed_on}.`);
  return parts.join(" ");
}

/**
 * Why a popularity cell is a dash. Two facts, two sentences: Apple was asked and
 * never offers the term, or Apple has not been asked about it yet.
 */
export function hintsAbsentTitle(h: KeywordHints | null, keyword: string): string {
  return h
    ? `Apple's suggestions never offer “${keyword}”, even typed in full. That is not the same as unpopular.`
    : `Not harvested yet: Apple's suggestion order for “${keyword}” has not been read.`;
}


function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
